import { useAtom } from "jotai";
import React, { memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { EItemsDiffType, EValueType, IDataItem, IPkLocalData, ITransformedData } from "@Types/specification";
import { Checkbox, Popover } from "antd";
import { createGlobalStyle } from "styled-components";
import { CaretRightOutlined, CloseOutlined } from "@ant-design/icons";
import { useLocalStorageState, useThrottle } from "ahooks";
import { initTracking } from "@Lib/jotai/tracking";
import { ColorValue, EquipIntroInfoModels, LegendItem, LongText, n, none, OptionalKit, RepeatedValue, TextValue, y } from "./common";
import { pkComparisonAtom } from "@Lib/jotai/pkCompare";

interface IProps {
  fetchData: IDataItem[];
  filteredTransformedData: ITransformedData;
  transformedData: ITransformedData;
  isFirstModelRef: React.RefObject<any>;
  searchRef: React.RefObject<any>;
  setTransformedData: (transformedData: ITransformedData) => void;
  setFilteredTransformedData: (filteredTransformedData: ITransformedData) => void;
  setOpenPK: (openPK: boolean) => void;
  onSectionChange: (title: string) => void;
  updateFilterCheckbox: (e) => void;
}


const GlobalStyle = createGlobalStyle`
  .ant-popover-inner-content {
    padding: 0;
  }
  .ant-popover-inner {
    border-radius: 4px;
  }
  .ant-checkbox-wrapper{
    margin-left: 0px!important;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #3264FF;
    border-color: #3264FF;
  }
  .ant-modal-content {
    border-radius: 4px !important;
  }
  .ant-modal-close-x {
    width: 38px;
    height: 48px;
    display: flex;
    align-items: center;
    font-size: 14px;
  }
`;

const itemsCls = "text-[#1D2029] text-14 flex items-center justify-start border-0 border-solid border-[#EDEFF5]";

const TableSpecification: React.FC<IProps> = (props) => {
  const { fetchData, filteredTransformedData, transformedData, setTransformedData, setFilteredTransformedData, setOpenPK, searchRef, onSectionChange, updateFilterCheckbox, isFirstModelRef } = props;

  const specRefs = useRef({});
  const itemRefs = useRef({});
  const [tracking] = useAtom(initTracking);
  const [pinnedData, setPinnedData] = useState<number[]>([]);
  const [pkDataList, setPkDataList] = useLocalStorageState<IPkLocalData[]>(`globalPkComparison`);
  const [pkDataIds, setPkDataIds] = useState<number[]>([]);
  const [scrollY, setScrollY] = useState(0);
  const [highlightedItemId, setHighlightedItemId] = useState<number | null>(null);
  const [itemsDiffCheckbox, setItemsDiffCheckbox] = useState<string[]>([EItemsDiffType.HIDE_NONE]);
  const [highlightItems, setHighlightItems] = useState<number[]>([]);
  const [isModalOpenEquip, setIsModalOpenEquip] = useState(false);
  const [equipIntroId, setEquipIntroId] = useState<number>(-1);
  const [globalPkList, updateGlobalPkList] = useAtom(pkComparisonAtom);

  const throttledScrollY = useThrottle(scrollY, { wait: 200 });

  const isPinnedMap = useMemo(() => new Set(pinnedData || []), [pinnedData]);
  const isHighlightedMap = useMemo(() => new Set(highlightItems || []), [highlightItems]);
  const isPkMap = useMemo(() => new Set(pkDataIds || []), [pkDataIds]);

  // 打开PK比较抽屉
  const showDrawer = (data) => {
    setOpenPK(true);
    updateGlobalPkList({
      data,
      callback: (data) => {
        setPkDataList(data)
      }
    });
  };

  // 固钉是否可点击状态
  const shouldDisplayImage = (id) => {
    if (isPinnedMap.size > 0) {
      const filteredData = filteredTransformedData.modelYearInfo.filter(
        (item) => !isPinnedMap.has(item.modelYearId)
      );
      return filteredData.findIndex((_item) => _item.modelYearId === id) === 0;
    }
    return false;
  };

  // 钉住元素
  const handleCellClick = (id) => {
    const newData = JSON.parse(JSON.stringify(transformedData));
    const pinnedCount = [...pinnedData];

    const index = newData.modelYearInfo.findIndex((item) => item.modelYearId === id);

    // 当前钉住的元素数量
    const clickedItem = newData.modelYearInfo[index];

    // 如果已经钉住并点击，则取消
    if (isPinnedMap.has(clickedItem.modelYearId)) {
      // 移动回到钉住区域之后的位置
      const deleteIndex = pinnedCount.findIndex((item) => item === clickedItem.modelYearId);
      newData.modelYearInfo.splice(index, 1); // 从当前位置移除
      newData.modelYearInfo.splice(pinnedCount.length - 1, 0, clickedItem); // 移动到已钉住区域之后
      deleteIndex !== -1 && pinnedCount.splice(deleteIndex, 1);
    } else {
      if (pinnedCount.length < 3) {
        newData.modelYearInfo.splice(index, 1);
        newData.modelYearInfo.splice(0, 0, clickedItem);
        pinnedCount.unshift(Number(clickedItem.modelYearId));
      } else {
        // 已经钉住三个元素，删除最后一个钉住的元素并添加新钉住元素
        pinnedCount.splice(pinnedData.length - 1, 1);
        newData.modelYearInfo.splice(index, 1);
        newData.modelYearInfo.splice(0, 0, clickedItem);
        pinnedCount.unshift(Number(clickedItem.modelYearId));
      }
    }

    newData.equipSpec.map((item) => {
      item.items.forEach((ii) => {
        // 获取要移动的项
        const clickedItem = JSON.parse(JSON.stringify(ii.itemss[index]));

        // 从原位置删除
        ii.itemss.splice(index, 1);

        if (isPinnedMap.has(clickedItem.modelYearId)) {
          // 如果该元素在 pinnedData 中，移动到已钉住区域之后
          ii.itemss.splice(pinnedData.length - 1, 0, clickedItem);
        } else {
          // 移动到开头
          ii.itemss.splice(0, 0, clickedItem);
        }
      });
      return item;
    });

    setPinnedData(pinnedCount);
    setTransformedData(newData);
  };


  // 左右移动
  const handleMoveClick = (id: number, type: 'left' | 'right') => {
    const newFormedData = JSON.parse(JSON.stringify(filteredTransformedData));
    const index = newFormedData.modelYearInfo.findIndex((item) => item.modelYearId === id);

    const itemToMove = newFormedData.modelYearInfo[index];
    if (index !== -1) {
      newFormedData.modelYearInfo.splice(index, 1);
      newFormedData.modelYearInfo.splice(type === 'left' ? index - 1 : index + 1, 0, itemToMove);

      newFormedData.equipSpec.map((item) => {
        item.items.map((i) => {
          const itemToMoveSpec = i.itemss[index];
          i.itemss.splice(index, 1);
          i.itemss.splice(type === 'left' ? index - 1 : index + 1, 0, itemToMoveSpec);
          return i;
        });
        return item;
      });
    }
    setFilteredTransformedData(newFormedData);
  };

  // 删除元素
  const handleCloseClick = (id) => {
    const newData = JSON.parse(JSON.stringify(transformedData));
    newData.modelYearInfo.splice(newData.modelYearInfo.findIndex((item) => item.modelYearId === id), 1);

    newData.equipSpec.map((item) => {
      item.items.map((i) => {
        i.itemss.splice(i.itemss.findIndex((item) => item.modelYearId === id), 1);
        return i;
      });
      return item;
    });

    setTransformedData(newData);
  }

  // 点击高亮不同项、隐藏相同项、隐藏暂无项
  const handleItemsDiffClick = (checkedValues) => {
    setItemsDiffCheckbox(checkedValues)
    updateFilterCheckbox(checkedValues)

    const highlightItems: any = new Set();
    if (checkedValues.includes('HighlightDiff')) {
      const newFormedData = JSON.parse(JSON.stringify(filteredTransformedData));

      newFormedData.equipSpec.map(spec => ({
        ...spec,
        items: spec.items
          .map(item => {
            const seenValues = new Set();
            let filteredItemss = item.itemss;

            filteredItemss = filteredItemss.filter(entry => {
              if (!seenValues.has(entry.value)) {
                seenValues.add(entry.value);
                return true;
              }
              return false;
            });

            !!filteredItemss.length && filteredItemss.length > 1 && highlightItems.add(item.entryId)

          })
      }));

      setHighlightItems([...highlightItems])
    } else {
      setHighlightItems([])
    }

    tracking && tracking.trackEvent({
      name: "element_clicked",
      params: {
        page_sub_business: "新車資料庫",
        page_business: "新車",
        trigger_id: "3750",
        page_id: "266",
        trigger_name: "規格配備列表篩選條件",
        next_spm: "3.266.692.1919",
        page: "規格配備頁",
        channel: "新車",
        element_id: "規格配備列表篩選條件",
        element: "按鈕",
        module: "規格配備列表篩選條件",
        tracker_method: "config"
      },
    });
  }

  useImperativeHandle(searchRef, () => ({
    // 搜索后定位高亮
    scrollToItem: (entryId) => {
      const targetRef = itemRefs.current[entryId];
      if (targetRef) {
        const targetOffsetTop = targetRef.getBoundingClientRect().top + window.scrollY - 244; // 直接计算目标位置

        window.scrollTo({
          left: 0,
          top: targetOffsetTop,
          behavior: 'smooth',
        });
        setHighlightedItemId(entryId);
      }
    },

    // 点击左侧栏定位
    clickLeftItem: (id) => {
      const targetRef = specRefs.current[id];
      if (targetRef) {
        const targetTop = targetRef.offsetTop;
        parent.scrollTo({
          top: targetTop - 120,
          behavior: 'smooth',
        });
      }
    },
  }));


  useEffect(() => {
    if (!(pkDataList || []).length) {
      setPkDataIds([]);
      return;
    };
    !!(pkDataList || []).length && setPkDataIds((pkDataList || []).map(item => item.myid));

  }, [pkDataList]);

  useEffect(() => {
    setPkDataList(globalPkList || []);
  }, [globalPkList]);

  useEffect(() => {
    const handleScroll = () => {
      requestAnimationFrame(() => {
        setScrollY(window.scrollY);
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll(".Section");
    let currentSection: any = null;

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      if (rect.top <= 212 && rect.bottom > 0) {
        currentSection = section.getAttribute("data-id") || null;
      }
    });
    !!currentSection && onSectionChange(currentSection);
  }, [throttledScrollY]);

  // 只剩一条车型数据，2个置灰并清除选中
  useEffect(() => {
    if (!!filteredTransformedData.modelYearInfo.length && filteredTransformedData.modelYearInfo.length === 1) {
      setHighlightItems([])
      itemsDiffCheckbox.includes(EItemsDiffType.HIDE_NONE) ? setItemsDiffCheckbox([EItemsDiffType.HIDE_NONE]) : setItemsDiffCheckbox([])
    }
  }, [filteredTransformedData]);

  // 原數據更新時，釘住初始化
  useEffect(() => {
    setPinnedData([]);
  }, [fetchData]);

  /*******************************************优化性能 start **********************************************/
  const getBgColorClass = useCallback((specs, itemss) => {
    const isHighlighted = highlightedItemId === specs.entryId || isHighlightedMap.has(specs.entryId);
    const isFirstModel = isFirstModelRef.current.isFirstGetId &&
      Number(isFirstModelRef.current.firstId) === Number(itemss.modelYearId);

    if (isHighlighted) {
      return 'bg-inherit';
    }
    if (isFirstModel) {
      return 'bg-[#F7FBFF]';
    }
    return 'bg-inherit';
  }, [highlightedItemId, isHighlightedMap]);

  const shouldDisplayLeftIcon = (inx, id) => {
    return inx === 0 || isPinnedMap.has(id) || shouldDisplayImage(id);
  };

  const shouldDisplayRightIcon = (inx, id) => {
    return inx === filteredTransformedData.modelYearInfo.length - 1 || isPinnedMap.has(id);
  };

  const getKindStyle = useCallback((model, inx) => {
    const isPinned = isPinnedMap.has(model.modelYearId);
    const isFirstModel = isFirstModelRef.current.isFirstGetId &&
      Number(isFirstModelRef.current.firstId) === Number(model.modelYearId);

    const left = isPinned ? `${179 + 210 + 210 * inx}px` : '';
    const zIndex = isPinned ? 1 : 0;
    const background = isFirstModel ? '#F7FBFF' : '#fff';

    return { left, zIndex, background };
  }, [isPinnedMap, isFirstModelRef]);

  const getLeftStyle = useCallback((itemss, indx) => {
    if (filteredTransformedData.modelYearInfo.length === 1) return '389px';
    if (isPinnedMap.has(itemss.modelYearId)) {
      return `${179 + 210 + 210 * indx}px`;
    }
    return '';
  }, [filteredTransformedData.modelYearInfo, isPinnedMap]);

  const getZIndex = useCallback((itemss, indx) => {
    return isPinnedMap.has(itemss.modelYearId) ? pinnedData.length - indx : 0;
  }, [isPinnedMap, pinnedData]);
  /*******************************************优化性能 end **********************************************/


  return (
    <div className='pl-14 w-auto bg-[#fff] pr-14'>
      <GlobalStyle />

      <div className='flex items-start justify-start bg-white sticky top-0 pt-8 z-[2]'>

        <div className="flex flex-col border-1 border-solid border-[#EDEFF5] rounded-tl-4 bg-[#fff] z-[1] sticky left-[178px] w-212">
          <div className="w-210  pl-14 pt-8 h-150">
            <p className="text-14 text-[#1D2029] mb-12 font-[600]">
              共<span className="text-20 text-[#F87F15]">{!!filteredTransformedData.modelYearInfo.length ? filteredTransformedData.modelYearInfo.length : 0}</span>款車型
            </p>
            <div className="flex flex-col items-start gap-14" id="tracking-itemsDiffCheckbox">
              <Checkbox.Group value={itemsDiffCheckbox} onChange={handleItemsDiffClick}>
                <div className='flex flex-col items-start gap-10 text-14 leading-20'>
                  <Checkbox value={EItemsDiffType.HIGHLIGHT_DIFF} disabled={filteredTransformedData.modelYearInfo.length === 1}>高亮不同項</Checkbox>
                  <Checkbox value={EItemsDiffType.HIDE_IDENTICAL} disabled={filteredTransformedData.modelYearInfo.length === 1}>隱藏相同項</Checkbox>
                  <Checkbox value={EItemsDiffType.HIDE_NONE}>隱藏暫無項</Checkbox>
                </div>
              </Checkbox.Group>
            </div>
          </div>
          <p className="w-210 h-42 border-0 border-t-1 border-solid border-[#EDEFF5] text-[#1D2029] text-14 flex items-center justify-start pl-14">建議售價</p>
        </div>
        {/* 车型数据 */}
        <div className="flex sticky left-[391px]">
          {Array.isArray(filteredTransformedData.modelYearInfo) && !!filteredTransformedData.modelYearInfo.length && filteredTransformedData.modelYearInfo.map((model, inx) => (
            <div
              style={getKindStyle(model, inx)}
              className={`flex flex-col border-0 border-t-1 border-r-1 border-b-1 border-solid border-[#EDEFF5] w-210 sticky`}
              key={model.modelYearId}
            >
              <div className="flex flex-col h-150">
                <div className={`w-full h-24 text-[#1D2029] text-12 flex items-center justify-between`}>
                  <div
                    className={`${!isPinnedMap.has(model.modelYearId) ? 'w-74 bg-[url(https://photo.8891.com.tw/2024/11/26/1732587905692194.png)]' : 'w-62 bg-[url(https://photo.8891.com.tw/2024/11/26/1732587905725984.png)]'} cursor-pointer bg-no-repeat bg-contain h-full pl-18 bg-14 flex items-center justify-start`}
                    onClick={() => handleCellClick(model.modelYearId)}
                  >
                    {!isPinnedMap.has(model.modelYearId) ? '釘在左側' : '已釘住'}
                  </div>
                  {filteredTransformedData.modelYearInfo.length > 1 && <CloseOutlined style={{ color: '#9DA2AF', fontSize: '12px' }} className="cursor-pointer mr-8" onClick={() => handleCloseClick(model.modelYearId)} />}
                </div>

                <LongText model={model} />

                {inx === 0 ?
                  <div data-id={model.kindId} className="w-full flex items-center justify-start px-14 gap-2 mb-10">
                    <a
                      href={`/kind-summary/${model.kindId}`}
                      className="text-14 text-[#3264FF] hover:text-[#3264FF]"
                      target="_blank"
                      id="tracking-SpecLinkSummary"
                    >
                      查看車款資訊
                    </a>
                    <CaretRightOutlined />
                  </div> : <div className="w-full h-18 mb-10"></div>}


                <div className="w-full flex items-center justify-between px-36">
                  <img
                    src={shouldDisplayLeftIcon(inx, model.modelYearId) ? 'https://photo.8891.com.tw/2024/11/15/1731656512361474.png' : 'https://photo.8891.com.tw/2024/11/15/1731657276896391.png'}
                    className={`w-30 h-30  ${shouldDisplayLeftIcon(inx, model.modelYearId) ? '' : 'cursor-pointer'}`}
                    onClick={() => !shouldDisplayLeftIcon(inx, model.modelYearId) && handleMoveClick(model.modelYearId, 'left')}
                    alt=""
                  />

                  <div id="tracking-AddToPKBtn" data-id={model.modelYearId} data-text={!isPkMap.has(model.modelYearId) ? '+比較' : '已加入'}>
                    {!isPkMap.has(model.modelYearId) ?
                      <span id={"AddToPKBtn"} className="flex items-center text-[#3264FF] text-14 cursor-pointer" onClick={() => showDrawer(model)}>
                        <img src="https://photo.8891.com.tw/2024/11/22/1732239634768589.png" width={8} height={8} alt="" className="mr-2" />
                        比較
                      </span> : <span id={"AddToPKBtn"} className="text-[#84A2FF] text-14 cursor-pointer" onClick={() => showDrawer(model)}>已加入</span>
                    }
                  </div>


                  <img
                    src={shouldDisplayRightIcon(inx, model.modelYearId) ? 'https://photo.8891.com.tw/2024/11/15/1731656512376625.png' : 'https://photo.8891.com.tw/2024/11/15/1731657276920346.png'}
                    className={`w-30 h-30  ${shouldDisplayRightIcon(inx, model.modelYearId) ? '' : 'cursor-pointer'}`}
                    onClick={() => !shouldDisplayRightIcon(inx, model.modelYearId) && handleMoveClick(model.modelYearId, 'right')}
                    alt=""
                  />
                </div>
              </div>
              <div className="w-full h-42 border-0 border-t-1 border-solid border-[#EDEFF5] flex items-center justify-between px-14 text-14">
                <span className={` ${model.price === '暫無報價' ? 'text-[#656873]' : 'text-[#F87F15] font-[600]'}`}>{model.price}</span>
                {model.saleStop && <span className="text-[#656873]">已停售</span>}
                {model.isInquiry === 1 && <a id="tracking-InquiryBtn" data-id={model.modelYearId} target="_blank" href={`/ask/price?b=${model.brandId}&k=${model.kindId}&source=規格配備頁一鍵詢價`} className="flex items-center justify-center w-64 h-24 bg-[#3264FF] rounded-4 text-12 text-white hover:text-white">一鍵詢價</a>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <>
        {Array.isArray(filteredTransformedData.equipSpec) && !!filteredTransformedData.equipSpec.length && filteredTransformedData.equipSpec.map((spec, idx) => (
          <div
            data-type={spec.name}
            data-id={spec.id}
            ref={(el) => (specRefs.current[spec.id] = el)}
            key={spec.id}
            className={`Section border-0 border-l-1 border-r-1 border-solid border-[#EDEFF5] w-fit sticky left-[179px]`}
          >
            {/* 第一层：基本數據、車身尺碼 */}
            {!!spec.items.length &&
              <div
                id="tracking-SpecType"
                data-name={spec.name}
                style={{
                  zIndex: `${filteredTransformedData.modelYearInfo.length + 1}`
                }}
                className={`top-[201px] sticky left-[179px] flex bg-[#F5F6FA] flex-1 border-0 border-b-1 border-solid border-[#EDEFF5]`}
              >
                <h3
                  className={`h-42 bg-[#F5F6FA] text-[#1D2029] text-14 flex items-center justify-start pl-14 sticky left-[179px] font-[600]`}
                >
                  {spec.name}
                  {spec.type === 2 && !!filteredTransformedData.modelYearInfo.length && (
                    <div className="ml-40 flex items-center justify-start gap-8">
                      <LegendItem icon={y} label="標配" />
                      <LegendItem icon={n} label="選配" />
                      <LegendItem icon={none} label="無" />
                    </div>
                  )}
                </h3>
              </div>
            }

            {/* 第2、3层：燃料稅、安全配備 */}
            <ul className='flex flex-col items-start '>
              {!!spec.items.length && spec.items.map((specs, specIdx) => (
                <li
                  ref={(el) => (itemRefs.current[specs.entryId] = el)}
                  key={specs.entryId}
                  className={`justify-between grid grid-flow-col auto-cols-[210px] min-h-42 border-0 border-b-1 border-solid border-[#EDEFF5] hover:bg-[#F1F8FF] hover:border-[#3264FF]
                     ${highlightedItemId === specs.entryId || isHighlightedMap.has(specs.entryId)
                      ? 'bg-[#F1F8FF]'
                      : specIdx % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'}`
                  }
                >
                  <p
                    id="tracking-SpecName"
                    className={`${itemsCls} ${!!specs.introId ? 'underline hover:text-[#3264FF] cursor-pointer' : ''} py-12 px-14 bg-inherit w-[210px] hover:bg-[#D7EAFF] border-r-1 sticky left-[179px] z-[1]`}
                    onClick={() => {
                      if (!!specs.introId) {
                        setEquipIntroId(specs.introId);
                        setIsModalOpenEquip(true);
                      }
                    }}
                  >
                    {specs.name}{!!specs.unit && `(${specs.unit})`}
                  </p>
                  {/* 第3层：具体数据 */}
                  {!!specs.itemss.length && specs.itemss.map((itemss, indx) => {
                    const borderClass = indx + 1 === specs.itemss.length ? "" : "border-r-1";
                    const bgColorClass = getBgColorClass(specs, itemss);

                    return (
                      <p
                        key={`${itemss.modelYearId}-${indx}`}
                        className={`${itemsCls} ${borderClass} ${bgColorClass} hover:bg-[#D7EAFF] sticky`}
                        style={{
                          left: getLeftStyle(itemss, indx),
                          zIndex: getZIndex(itemss, indx),
                        }}
                      >
                        {itemss.type === EValueType.TEXT && (
                          <TextValue value={itemss.value} y={y} n={n} none={none} />
                        )}
                        {itemss.type === EValueType.REPEATEDVALUE && (
                          <RepeatedValue value={itemss.value} y={y} n={n} />
                        )}
                        {itemss.type === EValueType.OPTIONALKIT && (
                          <OptionalKit value={itemss.value} />
                        )}
                        {itemss.type === EValueType.COLOR && <ColorValue value={itemss.value} />}
                      </p>
                    )
                  }
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </>

      {/* 安全装备介绍 */}
      <EquipIntroInfoModels
        equipIntroId={equipIntroId}
        isModalOpenEquip={isModalOpenEquip}
        setIsModalOpenEquip={setIsModalOpenEquip}
      />
    </div>
  );
};
export default memo(TableSpecification);
